import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtBRL, fmtCompetencia, currentCompetencia } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AlertTriangle, TrendingUp, TrendingDown, Building, DollarSign, Activity, PlayCircle, Clock, Flame, Printer, Eye, EyeOff } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, LineChart, Line, CartesianGrid, Cell } from "recharts";
import { toast } from "sonner";

const PRIVACY_KEY = "vmshopping.dashboard.privacy";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [kpis, setKpis] = useState(null);
  const [projecao, setProjecao] = useState(null);
  const [inadimplencia, setInadimplencia] = useState(null);
  const [indices, setIndices] = useState(null);
  const [loading, setLoading] = useState(false);
  const [privacyOn, setPrivacyOn] = useState(() => {
    try { return localStorage.getItem(PRIVACY_KEY) === "1"; } catch { return false; }
  });

  const togglePrivacy = () => {
    setPrivacyOn((v) => {
      const next = !v;
      try { localStorage.setItem(PRIVACY_KEY, next ? "1" : "0"); } catch { /* ignore */ }
      return next;
    });
  };

  // Formata valor monetário respeitando o modo privacidade
  const fmtMoney = (v) => (privacyOn ? "R$ ••••••" : fmtBRL(v));
  const fmtPercent = (v) => (privacyOn ? "••%" : `${v}%`);
  const fmtNumber = (v) => (privacyOn ? "••" : String(v));

  const load = async () => {
    setLoading(true);
    try {
      const [k, p, i] = await Promise.all([
        api.get("/dashboard/kpis"),
        api.get("/dashboard/projecao-90d"),
        api.get("/dashboard/inadimplencia"),
      ]);
      setKpis(k.data);
      setProjecao(p.data);
      setInadimplencia(i.data);
    } finally {
      setLoading(false);
    }
    try {
      const ind = await api.get("/dashboard/indices-economicos");
      setIndices(ind.data);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    // Carrega imediatamente ao montar
    load();
    // Polling: recarrega a cada 30 segundos para refletir pagamentos/webhooks em tempo real
    const interval = setInterval(() => load(), 30_000);
    // Recarrega quando a aba do navegador voltar ao foco (usuário volta do celular pro desktop)
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", onFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const executarFechamento = async () => {
    try {
      const { data } = await api.post(`/financeiro/fechamento/${currentCompetencia()}`);
      toast.success(`Fechamento executado — ${data.boletos_gerados} boleto(s) gerado(s)`);
      load();
    } catch (e) {
      toast.error("Falha no fechamento");
    }
  };

  if (!kpis) return <div className="text-muted-foreground">Carregando indicadores...</div>;

  const cor = kpis.termometro.cor;
  const dotClass = cor === "VERDE" ? "dot-verde" : cor === "AMARELO" ? "dot-amarelo" : "dot-vermelho";
  const termoLabel = cor === "VERDE" ? "text-success" : cor === "AMARELO" ? "text-warning" : "text-destructive";

  const chartData = projecao?.meses?.map((m) => ({
    mes: m.mes_label,
    Entrada: m.entrada_prevista,
    Fixas: m.despesas_fixas_projetadas,
    Variaveis: m.despesas_variaveis_projetadas,
    Saldo: m.saldo_projetado,
  })) || [];

  const unidadesChart = [
    { nome: "Ocupadas", qtd: kpis.unidades.ocupadas },
    { nome: "Livres", qtd: kpis.unidades.livres },
    { nome: "Obra", qtd: kpis.unidades.obra },
  ];

  return (
    <div className="space-y-8 fade-up">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-tag mb-2">Painel financeiro / Competência {fmtCompetencia(kpis.competencia)}</div>
          <h1 className="font-heading font-black tracking-tighter text-4xl sm:text-5xl">Dashboard</h1>
        </div>
        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
          <Button
            data-testid="btn-toggle-privacidade"
            onClick={togglePrivacy}
            variant="outline"
            size="icon"
            className="rounded-sm flex-shrink-0"
            title={privacyOn ? "Exibir valores" : "Ocultar valores (privacidade)"}
            aria-label={privacyOn ? "Exibir valores" : "Ocultar valores"}
          >
            {privacyOn ? <EyeOff size={16} /> : <Eye size={16} />}
          </Button>
          <Button data-testid="btn-executar-regua" onClick={() => navigate("/admin/whatsapp")} variant="outline" className="rounded-sm flex-1 sm:flex-none">
            <Activity size={14} className="mr-2" /> Régua de Cobrança
          </Button>
          <Button data-testid="btn-executar-fechamento" onClick={executarFechamento} className="rounded-sm bg-foreground text-background hover:bg-foreground/90 flex-1 sm:flex-none">
            <PlayCircle size={14} className="mr-2" /> Fechar Competência
          </Button>
        </div>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div data-testid="kpi-faturamento" className="stat-block p-6 rounded-sm">
          <div className="flex items-center justify-between">
            <span className="label-tag">Faturamento previsto/mês</span>
            <DollarSign size={16} className="text-muted-foreground" />
          </div>
          <div className="stat-num text-4xl mt-3">{fmtMoney(kpis.faturamento_bruto_mensal)}</div>
          <div className="text-xs text-muted-foreground mt-1">
            Boletos do mês (aluguel + rateio + IPTU + água)
          </div>
        </div>

        <div data-testid="kpi-vacancia" className="stat-block p-6 rounded-sm">
          <div className="flex items-center justify-between">
            <span className="label-tag">Taxa de vacância</span>
            <Building size={16} className="text-muted-foreground" />
          </div>
          <div className="stat-num text-4xl mt-3">{fmtPercent(kpis.unidades.vacancia_percent)}</div>
          <div className="text-xs text-muted-foreground mt-1">
            {kpis.unidades.livres} livres de {kpis.unidades.total}
          </div>
        </div>

        <div data-testid="kpi-custo-vacancia" className="stat-block p-6 rounded-sm">
          <div className="flex items-center justify-between">
            <span className="label-tag">Custo de vacância</span>
            <TrendingDown size={16} className="text-destructive" />
          </div>
          <div className="stat-num text-4xl mt-3 text-destructive">{fmtMoney(kpis.custo_vacancia)}</div>
          <div className="text-xs text-muted-foreground mt-1">Rateio absorvido pelo shopping</div>
        </div>

        <div data-testid="kpi-termometro" className="stat-block p-6 rounded-sm">
          <div className="flex items-center justify-between">
            <span className="label-tag">Termômetro inadimplência</span>
            <div className={`w-3 h-3 rounded-full ${dotClass}`} />
          </div>
          <div className={`stat-num text-4xl mt-3 ${termoLabel}`}>{fmtPercent(kpis.termometro.inadimplencia_percent)}</div>
          <div className="text-xs mt-1 font-semibold uppercase tracking-widest">{kpis.termometro.label}</div>
        </div>
      </div>

      {/* Composição do faturamento previsto — Recebido · A Vencer · Vencido */}
      {kpis.boletos && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div data-testid="kpi-recebido" className="stat-block p-5 rounded-sm border-success/30">
            <div className="label-tag">Recebido (efetivado)</div>
            <div className="stat-num text-2xl mt-2 text-success">{fmtMoney(kpis.boletos.total_pago)}</div>
            <div className="text-[10px] text-muted-foreground mt-1">
              {kpis.boletos.quantidade_pagos ?? 0} boleto(s) PAGO(s) na competência
            </div>
          </div>
          <div data-testid="kpi-a-vencer" className="stat-block p-5 rounded-sm border-info/30">
            <div className="label-tag">A vencer (no prazo)</div>
            <div className="stat-num text-2xl mt-2 text-info">{fmtMoney(kpis.boletos.total_a_vencer ?? 0)}</div>
            <div className="text-[10px] text-muted-foreground mt-1">
              {kpis.boletos.quantidade_a_vencer ?? 0} boleto(s) ABERTO(s), venc. ≥ hoje
            </div>
          </div>
          <div data-testid="kpi-em-atraso" className="stat-block p-5 rounded-sm border-destructive/30">
            <div className="label-tag">Em atraso (inadimplência)</div>
            <div className="stat-num text-2xl mt-2 text-destructive">{fmtMoney(kpis.boletos.total_vencido)}</div>
            <div className="text-[10px] text-muted-foreground mt-1">
              {kpis.boletos.quantidade_vencidos ?? 0} boleto(s) com vencimento passado
            </div>
          </div>
        </div>
      )}

      {/* DASHBOARD DE INADIMPLÊNCIA EM TEMPO REAL */}
      {inadimplencia && (
        <section data-testid="painel-inadimplencia" className="space-y-4 print-container">
          <div className="flex items-end justify-between flex-wrap gap-2">
            <div>
              <div className="label-tag mb-1 flex items-center gap-2">
                <Flame size={12} className="text-destructive" />
                Exposição financeira em tempo real
              </div>
              <h2 className="font-heading font-black tracking-tighter text-2xl">Inadimplência</h2>
            </div>
            <div className="text-[10px] text-muted-foreground font-mono">
              Atualizado {new Date(inadimplencia.gerado_em).toLocaleTimeString("pt-BR")}
            </div>
            <Button
              data-testid="btn-imprimir-inadimplencia"
              onClick={() => window.print()}
              variant="outline"
              size="sm"
              className="rounded-sm no-print"
            >
              <Printer size={12} className="mr-2" /> Imprimir Relatório
            </Button>
          </div>

          {/* Cabeçalho apenas em modo impressão */}
          <div className="print-only mb-6">
            <div style={{textAlign:"center", borderBottom:"2px solid #000", paddingBottom:12, marginBottom:16}}>
              <h1 style={{fontSize:20, fontWeight:900, letterSpacing:"-0.02em"}}>VM PIABETÁ SHOPPING CENTER LTDA</h1>
              <div style={{fontSize:11, marginTop:4}}>Relatório de Inadimplência · Gerado em {new Date(inadimplencia.gerado_em).toLocaleString("pt-BR")}</div>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div data-testid="inad-qtd" className="stat-block p-5 rounded-sm border-l-4 border-l-destructive">
              <div className="label-tag">Boletos vencidos</div>
              <div className="stat-num text-3xl mt-2 text-destructive">{fmtNumber(inadimplencia.resumo.qtd_vencidos)}</div>
              <div className="text-[10px] text-muted-foreground mt-1">
                de {fmtNumber(inadimplencia.resumo.qtd_boletos_abertos)} em aberto ({fmtPercent(inadimplencia.resumo.taxa_inadimplencia_percent)})
              </div>
            </div>
            <div data-testid="inad-total" className="stat-block p-5 rounded-sm border-l-4 border-l-destructive">
              <div className="label-tag">Valor total em atraso</div>
              <div className="stat-num text-3xl mt-2 text-destructive">{fmtMoney(inadimplencia.resumo.valor_atualizado_total)}</div>
              <div className="text-[10px] text-muted-foreground mt-1">
                Base {fmtMoney(inadimplencia.resumo.valor_base_total)}
              </div>
            </div>
            <div data-testid="inad-encargos" className="stat-block p-5 rounded-sm border-l-4 border-l-warning">
              <div className="label-tag">Encargos acumulados</div>
              <div className="stat-num text-3xl mt-2 text-warning">{fmtMoney(inadimplencia.resumo.encargos_acumulados)}</div>
              <div className="text-[10px] text-muted-foreground mt-1">
                Multa {fmtMoney(inadimplencia.resumo.multa_total)} · Mora {fmtMoney(inadimplencia.resumo.mora_total)}
              </div>
            </div>
            <div data-testid="inad-ticket" className="stat-block p-5 rounded-sm border-l-4 border-l-info">
              <div className="label-tag">Ticket médio em atraso</div>
              <div className="stat-num text-3xl mt-2 text-info">{fmtMoney(inadimplencia.resumo.ticket_medio)}</div>
              <div className="text-[10px] text-muted-foreground mt-1">valor atualizado ÷ nº de vencidos</div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Distribuição por faixa etária */}
            <Card className="p-6 rounded-sm border-border">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="label-tag mb-1">Aging</div>
                  <h3 className="font-heading font-bold text-lg">Distribuição por dias em atraso</h3>
                </div>
                <Clock size={16} className="text-muted-foreground" />
              </div>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={inadimplencia.distribuicao_faixas} barCategoryGap="30%">
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="faixa" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} allowDecimals={false} />
                    <Tooltip
                      contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 4 }}
                      formatter={(v, name) => name === "valor" ? fmtBRL(v) : v}
                    />
                    <Bar dataKey="qtd" name="Boletos">
                      {inadimplencia.distribuicao_faixas.map((entry, idx) => {
                        const cores = ["hsl(var(--warning))", "hsl(var(--warning))", "hsl(var(--destructive))", "hsl(var(--destructive))"];
                        return <Cell key={`cell-${idx}`} fill={cores[idx]} />;
                      })}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-3 grid grid-cols-4 gap-1 text-center">
                {inadimplencia.distribuicao_faixas.map((f) => (
                  <div key={f.faixa} className="text-[10px]">
                    <div className="font-mono text-muted-foreground">{f.faixa}</div>
                    <div className="font-bold">{fmtBRL(f.valor)}</div>
                  </div>
                ))}
              </div>
            </Card>

            {/* Top 5 devedores */}
            <Card className="lg:col-span-2 p-6 rounded-sm border-border">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="label-tag mb-1">Cobrança prioritária</div>
                  <h3 className="font-heading font-bold text-lg">Top 5 lojistas em atraso</h3>
                </div>
                <AlertTriangle size={16} className="text-destructive" />
              </div>
              {inadimplencia.top_devedores.length === 0 ? (
                <div className="py-10 text-center text-sm text-muted-foreground border border-dashed border-border rounded-sm">
                  Nenhum lojista em atraso 🟢
                </div>
              ) : (
                <div className="space-y-2" data-testid="top-devedores">
                  {inadimplencia.top_devedores.map((d, idx) => (
                    <div
                      key={d.boleto_id}
                      data-testid={`devedor-${idx}`}
                      className="flex items-center gap-3 border border-border p-3 rounded-sm hover:bg-accent/50 transition-colors"
                    >
                      <div className="w-8 h-8 flex items-center justify-center rounded-sm bg-destructive/10 text-destructive font-heading font-black text-sm flex-shrink-0">
                        {idx + 1}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-sm break-anywhere">{d.razao_social}</div>
                        <div className="text-[11px] text-muted-foreground font-mono">
                          {d.cnpj} · {fmtCompetencia(d.competencia)} · venc {d.vencimento}
                        </div>
                        <div className="text-[10px] text-muted-foreground mt-0.5">
                          Base {fmtBRL(d.valor_base)} + Multa {fmtBRL(d.multa)} + Mora {fmtBRL(d.mora)}
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="stat-num text-lg text-destructive">{fmtBRL(d.valor_atualizado)}</div>
                        <Badge variant="destructive" className="text-[10px] uppercase tracking-widest mt-1">
                          {d.dias_atraso}d atraso
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        </section>
      )}

      {/* Chart row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 p-6 rounded-sm border-border">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="label-tag mb-1">Projeção 90 dias</div>
              <h2 className="font-heading font-bold text-xl">Fluxo de caixa projetado</h2>
            </div>
            <TrendingUp size={18} className="text-muted-foreground" />
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="mes" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickFormatter={(v) => `R$${(v/1000).toFixed(0)}k`} />
                <Tooltip
                  contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 4 }}
                  formatter={(v) => fmtBRL(v)}
                />
                <Line type="monotone" dataKey="Entrada" stroke="hsl(var(--success))" strokeWidth={2} dot={{ r: 4 }} />
                <Line type="monotone" dataKey="Fixas" stroke="hsl(var(--warning))" strokeWidth={2} dot={{ r: 4 }} />
                <Line type="monotone" dataKey="Variaveis" stroke="hsl(var(--info))" strokeWidth={2} dot={{ r: 4 }} />
                <Line type="monotone" dataKey="Saldo" stroke="hsl(var(--foreground))" strokeWidth={3} dot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6 rounded-sm border-border">
          <div className="label-tag mb-1">Ocupação atual</div>
          <h2 className="font-heading font-bold text-xl mb-6">Unidades</h2>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={unidadesChart}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="nome" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 4 }} />
                <Bar dataKey="qtd" fill="hsl(var(--foreground))" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-4 grid grid-cols-3 gap-2 text-center">
            <div>
              <div className="stat-num text-2xl text-success">{kpis.unidades.ocupadas}</div>
              <div className="label-tag mt-1">Ocup.</div>
            </div>
            <div>
              <div className="stat-num text-2xl">{kpis.unidades.livres}</div>
              <div className="label-tag mt-1">Livres</div>
            </div>
            <div>
              <div className="stat-num text-2xl text-warning">{kpis.unidades.obra}</div>
              <div className="label-tag mt-1">Obra</div>
            </div>
          </div>
        </Card>
      </div>

      {/* Índices econômicos */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-6 rounded-sm border-border lg:col-span-3">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="label-tag mb-1">Fonte BCB</div>
              <h2 className="font-heading font-bold text-xl">Índices econômicos</h2>
            </div>
            {indices?.atualizado_em && (
              <div className="text-[10px] text-muted-foreground font-mono">
                Atualizado {indices.atualizado_em?.slice(0, 16)}
              </div>
            )}
          </div>
          {indices ? (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
              {["IPCA", "IGP-M", "SELIC"].map((k) => (
                <div key={k} className="flex items-center justify-between border-b border-border/60 pb-3">
                  <div className="label-tag">{k} 12m</div>
                  <div className="stat-num text-2xl">
                    {indices[k] != null ? `${indices[k].toFixed(2)}%` : "—"}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">Buscando na API do Banco Central...</div>
          )}
        </Card>
      </div>
    </div>
  );
}
